import { useListMappingsQuery } from 'pages/Settings/Account/components/FieldMapper/api/hooks/useListMappingsQuery';
import {
  Cloud,
  Domain,
  Operation,
} from 'pages/Settings/Account/components/FieldMapper/utils/constants';
import { useQuery } from '@tanstack/react-query';
import { getMsftReferralMappedFields } from 'packages/tackle-api/src/data-mapper/getMsftReferralMappedFields';
import { MsftReferral_Create_MappedFields_Response_TransformSchema } from './transformations';
type OperationOption = Operation.CREATE_OPPORTUNITY;

const cloud = Cloud.MSFT;
const domain = Domain.CO_SELL;

export const useMsftReferralMappedFields = ({
  crmId,
  /** disabled by default bc user must have a Salesforce vendor connected to enable */
  enabled = false,
  operation = Operation.CREATE_OPPORTUNITY,
}: {
  crmId?: string;
  enabled: boolean;
  operation?: OperationOption;
}) => {
  /** first check if the vendor has any mappings,
   * then only fetch mappings if a mappingId is available.
   */
  const listMappingQuery = useListMappingsQuery({
    cloud,
    domain,
    operation,
    isDefault: true,
    isPublished: true,
    enabled: enabled && !!crmId && !!operation,
  });

  const mappingId = listMappingQuery.data?.mappings?.[0]?.mappingId;

  const mappedFieldsQuery = useQuery({
    queryKey: ['mapped-fields', crmId, mappingId, cloud, domain, operation],
    queryFn: () => getMsftReferralMappedFields({ crmId, mappingId, operation }),
    select: (data) => {
      if (operation === Operation.CREATE_OPPORTUNITY) {
        return MsftReferral_Create_MappedFields_Response_TransformSchema.parse(
          data,
        );
      }
      return null;
    },
    retry: false,
    /** only load this 1 time since we are using it to load initial values. */
    staleTime: Infinity,
    enabled: !!mappingId,
  });

  return {
    isInitialLoading:
      listMappingQuery.isInitialLoading || mappedFieldsQuery.isInitialLoading,
    mappedData: mappedFieldsQuery.data,
    isError: mappedFieldsQuery.isError || listMappingQuery.isError,
    isRefetching:
      mappedFieldsQuery.isRefetching || listMappingQuery.isRefetching,
    refetch: mappedFieldsQuery.isError
      ? mappedFieldsQuery.refetch
      : listMappingQuery.refetch,
  };
};
